import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

import AccountImageUpload from "@/components/account-image-upload";
import Button from "@/components/common/input/button";
import Input from "@/components/common/input/input";
import Spinner from "@/components/common/spinner";
import { handled, logRejection } from "@/helpers";
import { useAuth } from "@/hooks/auth";
import useRecaptcha from "@/hooks/recaptcha";
import { showSnackbar } from "@/hooks/snackbar";
import google_logo from "@/img/google-square.svg";
import msft_logo from "@/img/microsoft-square.svg";

export default function CreateUser() {
  const { user, is_loading, error, createUserTokenValidation } = useAuth();
  const { token } = useParams();
  const { hide: reHide, verify: reVerify, loading: reLoading } = useRecaptcha();
  const [validation_done, setValidationDone] = useState(false);
  const [manual_creation, setManualCreation] = useState(false);
  const navigate = useNavigate();

  // The gate is sticky. `user.roles` loses "creating-user" the moment the account
  // is created, which is a render or two before doneCB has navigated away, so
  // reading it directly would flash the token screen back up on the way out.
  if (!validation_done && user?.roles.includes("creating-user")) {
    setValidationDone(true);
  }

  const doneCB = () => {
    const url = new URL(window.location.href);
    // do we have a to param?
    const to_param = url.searchParams.get("to");
    reHide();
    void navigate(to_param ? to_param : "/", { replace: true });
  };

  const extauthclass = clsx(
    "flex w-72 cursor-pointer items-center justify-center gap-4 rounded-md border-2 px-4 py-2",
    "transition-btn",
    "duration-150",
    "ease-out",
    "hover:ring-4",
    "hover:ring-offset-0",
    "hover:ring-[hsl(210deg_40%_10%_/_0.25)]",
  );

  return (
    <div>
      {(reLoading || is_loading) && (
        <div className="relative flex h-screen w-screen">
          <div className="absolute bottom-0 translate-y-[-50%] md:left-[50%]">
            <Spinner large />
          </div>
        </div>
      )}
      {!validation_done && (
        <div className="mx-auto flex min-h-screen max-w-[36rem] flex-col items-center justify-center">
          <img
            src="https://storage.googleapis.com/tacolab-dev-customer-resources/assets/logo/Unfold_logo_white.png"
            alt="Unfold logotype"
            className="mb-24 w-80"
          />
          <div className="flex justify-center">
            <Button
              onClick={() => {
                if (!token) return;
                reVerify()
                  .then((result) => createUserTokenValidation(token, result))
                  .then(reHide)
                  .catch(logRejection);
              }}
            >
              Enter
            </Button>
          </div>
          {error && <div>Something went wrong. Check the link and try again.</div>}
        </div>
      )}
      {validation_done && (
        <>
          {!manual_creation && (
            <div className="mx-auto flex min-h-screen w-[32rem] flex-col items-center justify-center">
              <div className="my-2">
                <a className={extauthclass} href="/auth/google/create">
                  <img className="w-6" src={google_logo} alt="Google logotype" /> Continue with
                  Google
                </a>
              </div>
              <div className="my-2">
                <a className={extauthclass} href="/auth/microsoft/create">
                  <img className="w-6" src={msft_logo} alt="Microsoft logotype" /> Continue with
                  Microsoft
                </a>
              </div>
              <p>
                Or you can{" "}
                <button
                  type="button"
                  onClick={() => setManualCreation(true)}
                  className="cursor-pointer font-medium text-future-purple"
                >
                  create an account manually
                </button>
                .
              </p>
            </div>
          )}
          {manual_creation && (
            <div className="mx-auto my-12 flex max-w-[36rem] flex-col items-center">
              <CreateUserForm onSuccessfullSubmit={doneCB} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

const validate_passwords = (first: string, second: string) => {
  if (first !== second) {
    return "The passwords do not match";
  }
};

interface CreateUserFormValues {
  name: string;
  password: string;
  password_again: string;
}

interface CreateUserFormProps {
  onSuccessfullSubmit: () => void;
}

const CreateUserForm = ({ onSuccessfullSubmit }: CreateUserFormProps) => {
  const { user, updateUser } = useAuth();
  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<CreateUserFormValues>();
  const [photo, setPhoto] = useState<File | string | null>(user?.photo_url ?? null);
  if (!user) return null;

  const _handleSubmit = (form_data: CreateUserFormValues) => {
    clearErrors();
    updateUser(form_data.name, "", photo, false, form_data.password)
      .then((success) => {
        if (success) {
          onSuccessfullSubmit();
        } else {
          console.error("Error updating user");
          showSnackbar("Could not create the account. Please try again.", "warning");
        }
      })
      .catch(logRejection);
  };

  return (
    <div>
      <h1 className="text-primary mb-1 text-xl md:text-2xl">Create Account</h1>
      <form onSubmit={handled(handleSubmit(_handleSubmit))}>
        <div className="md:mb-12">
          <Input label="Email" id="email" type="email" disabled value={user.email} required />
          <Input label="Name" id="name" register={register} required />
        </div>
        <div className="md:mb-12">
          <Input
            label="Password"
            id="password"
            type="password"
            register={register}
            register_options={{ required: "Password is required" }}
            error={errors.password?.message}
            required
          />
          <Input
            label="Repeat password"
            id="password_again"
            type="password"
            register={register}
            register_options={{
              required: "Please repeat the password",
              validate: (value: string, form_values: CreateUserFormValues) =>
                validate_passwords(form_values.password, value),
            }}
            error={errors.password_again?.message}
            required
          />
        </div>
        <div className="md:mb-12">
          <div className="mb-2 inline-block leading-relaxed">Photo</div>
          <AccountImageUpload value={photo} onChange={setPhoto} />
        </div>
        <div className="flex justify-end">
          <Button type="submit">Submit</Button>
        </div>
      </form>
    </div>
  );
};
